import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  X, Upload, FileJson, CheckCircle2, AlertTriangle, AlertCircle, Loader2,
  Download, Plus, Minus, PenLine, ShieldCheck,
} from 'lucide-react'
import { nsApi, templatesApi } from '../services/api'

// Import (create or update) a NetSuite template from one JSON template
// package — format "xtract-template/1", documented in
// docs/TEMPLATE_JSON_GUIDE.md. Validation runs entirely on the server (the
// same engine the CLI uses); this screen only shows what it found and what
// applying the file would change.

// A minimal starting point for "Start from an example".
const STARTER_PACKAGE = {
  format: 'xtract-template/1',
  template: {
    name: 'My NetSuite template',
    description: 'What this template extracts and where it posts.',
    settings: { require_approval: true, validate_before_posting: true, block_on_errors: true, apply_defaults: true, mandate_review: true },
    document: { dates: { format: 'D/M/YYYY' }, currency: { strip_symbols: ['AUD'], decimal_separator: '.', thousands_separator: ',' } },
  },
  record: { ns_entity_type: 'vendor_bid', name_source: 'from_filename' },
  header: [
    { key: 'opp_no', label: 'Bid Number', type: 'text', ns_field: 'custrecord_jcs_sp_bids', required: true, extraction_hint: 'The bid / opportunity number exactly as printed.' },
    { key: 'supplier', label: 'Supplier', ns_field: 'custrecord_jcs_sp_supplier', source: 'constant', value: '11711', resolve: { lookup_entity: 'vendors' } },
  ],
  lines: {
    field_name: 'line_items',
    label: 'Line Items',
    description: 'One row per product.',
    columns: [
      { key: 'item_code', label: 'Item Code', type: 'text', ns_field: 'custrecord_jcs_spil_item_code', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', ns_field: 'custrecord_jcs_spil_quantity', required: true },
    ],
  },
  excluded: [{ ns_field: 'custrecord_jcs_sp_total_price', reason: 'NetSuite computes the total itself.' }],
}

const btnPrimary = 'inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-navy-800 text-white text-sm font-semibold shadow-sm transition-all duration-200 hover:bg-navy-900 disabled:opacity-50 disabled:cursor-not-allowed'
const btnSecondary = 'inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'

function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2) + '\n'], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export function exportTemplateJson(templateId, templateName) {
  return templatesApi.exportSpec(templateId).then(({ data }) => {
    const safe = String(templateName || data?.template?.name || `template_${templateId}`)
      .replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '')
    downloadJson(data, `${safe || 'template'}.template.json`)
    return data
  })
}

function errorMessage(err, fallback) {
  const data = err?.response?.data
  return data?.error || (Array.isArray(data?.errors) && data.errors[0]) || err?.message || fallback
}

function DiffSection({ title, diff }) {
  if (!diff) return null
  const { added = [], removed = [], changed = [] } = diff
  if (!added.length && !removed.length && !changed.length) return null
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{title}</div>
      <ul className="space-y-1 text-sm">
        {added.map((k) => (
          <li key={`a-${k}`} className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
            <Plus className="h-3.5 w-3.5 shrink-0" /> <code>{k}</code>
          </li>
        ))}
        {removed.map((k) => (
          <li key={`r-${k}`} className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <Minus className="h-3.5 w-3.5 shrink-0" /> <code>{k}</code>
          </li>
        ))}
        {changed.map((c) => (
          <li key={`c-${c.key}`} className="text-amber-700 dark:text-amber-400">
            <div className="flex items-center gap-2"><PenLine className="h-3.5 w-3.5 shrink-0" /> <code>{c.key}</code></div>
            <ul className="ml-6 text-xs text-gray-600 dark:text-gray-400">
              {Object.entries(c.changes).map(([attr, v]) => (
                <li key={attr}>{attr}: <span className="line-through">{String(v.from ?? '—')}</span> → {String(v.to ?? '—')}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function TemplateJsonImportModal({ onClose, onApplied, templateId = null, templateName = '' }) {
  const isUpdate = Boolean(templateId)
  const [text, setText] = useState('')
  const [fileName, setFileName] = useState('')
  const [validation, setValidation] = useState(null)
  const [validatedText, setValidatedText] = useState(null)
  const [validating, setValidating] = useState(false)
  const [applying, setApplying] = useState(false)
  const [applyResult, setApplyResult] = useState(null)
  const [buildAnalyzer, setBuildAnalyzer] = useState(true)
  const [analyzerState, setAnalyzerState] = useState(null) // null | 'building' | 'built' | message
  const [requestError, setRequestError] = useState(null)
  const [recordTypes, setRecordTypes] = useState([])
  const [legacyRecordType, setLegacyRecordType] = useState('')
  const [legacyName, setLegacyName] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef(null)

  const parsed = useMemo(() => {
    if (!text.trim()) return { value: null, error: null }
    try {
      return { value: JSON.parse(text), error: null }
    } catch (e) {
      return { value: null, error: e.message }
    }
  }, [text])
  const isLegacy = Array.isArray(parsed.value)

  useEffect(() => {
    nsApi.getEntityTypes()
      .then(({ data }) => setRecordTypes(data?.entity_types || []))
      .catch(() => setRecordTypes([]))
  }, [])

  const loadFile = useCallback((file) => {
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = () => {
      setText(String(reader.result || ''))
      setValidation(null)
      setApplyResult(null)
    }
    reader.readAsText(file)
  }, [])

  const loadStarter = () => {
    setText(JSON.stringify(STARTER_PACKAGE, null, 2))
    setFileName('')
    setValidation(null)
  }

  const requestBody = () => {
    const body = { package: parsed.value }
    if (templateId) body.template_id = templateId
    if (isLegacy) {
      body.legacy_record_type = legacyRecordType
      body.legacy_name = legacyName || fileName.replace(/\.json$/i, '') || 'Imported template'
    }
    return body
  }

  const handleValidate = async () => {
    setRequestError(null)
    setApplyResult(null)
    setValidating(true)
    try {
      const { data } = await templatesApi.validateSpec(requestBody())
      setValidation(data)
      setValidatedText(text)
    } catch (err) {
      setRequestError(errorMessage(err, 'Validation failed.'))
      setValidation(null)
    } finally {
      setValidating(false)
    }
  }

  const useConverted = () => {
    if (!validation?.converted_package) return
    setText(JSON.stringify(validation.converted_package, null, 2))
    setValidation(null)
  }

  const handleApply = async () => {
    setRequestError(null)
    setApplying(true)
    try {
      const { data } = await templatesApi.applySpec(requestBody())
      setApplyResult(data)
      if (buildAnalyzer && data?.template_id) {
        setAnalyzerState('building')
        try {
          await templatesApi.buildAnalyzer(data.template_id)
          setAnalyzerState('built')
        } catch (err) {
          setAnalyzerState(errorMessage(err, 'Analyzer build failed — build it from the template page.'))
        }
      }
      onApplied?.(data)
    } catch (err) {
      const errs = err?.response?.data?.errors
      if (Array.isArray(errs) && errs.length) {
        setValidation((v) => ({ ...(v || {}), ok: false, errors: errs, warnings: err.response.data.warnings || [] }))
      }
      setRequestError(errorMessage(err, 'Could not apply the template JSON.'))
    } finally {
      setApplying(false)
    }
  }

  const isStale = validation && validatedText !== text
  const canApply = validation?.ok && !isStale && !applying && !applyResult && !parsed.error
  const canValidate = Boolean(parsed.value) && !validating && (!isLegacy || legacyRecordType)
  const diff = validation?.diff

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                <FileJson className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isUpdate ? 'Update template from JSON' : 'Create template from JSON'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {isUpdate
                    ? <>Applies the file to <span className="font-medium">{templateName || `template ${templateId}`}</span>. Fields and rules not in the file are removed.</>
                    : 'One file sets the record type, header mappings, line items, constants and excluded fields.'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragOver(false); loadFile(e.dataTransfer.files?.[0]) }}
              className={`rounded-xl border-2 border-dashed p-4 flex flex-wrap items-center justify-between gap-3 transition-colors ${isDragOver ? 'border-navy-500 bg-navy-50 dark:bg-navy-900/20' : 'border-gray-200 dark:border-gray-700'}`}
            >
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <Upload className="h-4 w-4" />
                {fileName ? <span className="font-medium">{fileName}</span> : 'Drop a .json file here, or paste it below'}
              </div>
              <div className="flex gap-2">
                <button type="button" className={btnSecondary} onClick={() => fileInputRef.current?.click()}>Choose file</button>
                {!isUpdate && <button type="button" className={btnSecondary} onClick={loadStarter}>Start from an example</button>}
              </div>
              <input ref={fileInputRef} type="file" accept=".json,application/json" className="hidden"
                onChange={(e) => { loadFile(e.target.files?.[0]); e.target.value = '' }} />
            </div>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              spellCheck={false}
              placeholder='{ "format": "xtract-template/1", ... }'
              className="w-full h-64 font-mono text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 p-3 focus:outline-none focus:ring-2 focus:ring-navy-500"
            />
            {parsed.error && (
              <div className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" /> Not valid JSON: {parsed.error}
              </div>
            )}

            {isLegacy && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  This is the older field-list format. It has no record type, constants, exclusions or date format,
                  so it will be converted to a template package for you to finish.
                </p>
                <div className="flex flex-wrap gap-2">
                  <select value={legacyRecordType} onChange={(e) => setLegacyRecordType(e.target.value)}
                    className="h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm px-2">
                    <option value="">Record type…</option>
                    {recordTypes.map((t) => (
                      <option key={t.ns_entity_type_key} value={t.ns_entity_type_key}>{t.display_name}</option>
                    ))}
                  </select>
                  {!isUpdate && (
                    <input value={legacyName} onChange={(e) => setLegacyName(e.target.value)} placeholder="Template name"
                      className="h-9 flex-1 min-w-[12rem] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm px-2" />
                  )}
                </div>
              </div>
            )}

            {requestError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {requestError}
              </div>
            )}

            {validation && (
              <div className="space-y-3">
                {isStale && (
                  <div className="text-sm text-amber-700 dark:text-amber-300">The JSON changed since it was checked — validate again.</div>
                )}
                {validation.ok ? (
                  <div className="flex flex-wrap items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-medium">No problems found.</span>
                    {validation.record_type && <span className="text-gray-600 dark:text-gray-300">Record type: {validation.record_type.label}</span>}
                  </div>
                ) : (
                  <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                    <div className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">
                      {validation.errors?.length} problem{validation.errors?.length === 1 ? '' : 's'} to fix before this can be applied
                    </div>
                    <ul className="list-disc ml-5 text-sm text-red-700 dark:text-red-300 space-y-0.5">
                      {(validation.errors || []).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
                {validation.warnings?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
                      <AlertTriangle className="h-4 w-4" /> Notes
                    </div>
                    <ul className="list-disc ml-5 text-sm text-amber-800 dark:text-amber-200 space-y-0.5">
                      {validation.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                {validation.converted_package && (
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className={btnSecondary} onClick={useConverted}>Edit the converted JSON here</button>
                    <button type="button" className={btnSecondary}
                      onClick={() => downloadJson(validation.converted_package, `${(legacyName || 'converted').replace(/[^A-Za-z0-9._-]+/g, '_')}.template.json`)}>
                      <Download className="h-4 w-4" /> Download converted JSON
                    </button>
                  </div>
                )}
                {validation.summary && (
                  <div className="flex flex-wrap gap-2 text-xs">
                    {[['header_fields', 'header fields'], ['constants', 'constants'], ['line_columns', 'line columns'], ['excluded', 'excluded']].map(([k, label]) => (
                      <span key={k} className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                        {validation.summary[k]} {label}
                      </span>
                    ))}
                  </div>
                )}
                {diff && isUpdate && (
                  <div className="space-y-2">
                    {diff.record?.length > 0 && (
                      <div className="rounded-lg border border-purple-200 dark:border-purple-800 p-3 text-sm">
                        <div className="text-xs font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-300 mb-1">Record</div>
                        {diff.record.map((r) => (
                          <div key={r.field}><code>{r.field}</code>: <span className="line-through">{String(r.from ?? '—')}</span> → {String(r.to)}</div>
                        ))}
                        {diff.record.some((r) => r.field === 'ns_entity_type') && (
                          <div className="mt-1 text-xs text-purple-700 dark:text-purple-300">Changing the record type replaces every NetSuite rule on this template.</div>
                        )}
                      </div>
                    )}
                    <div className="grid gap-2 sm:grid-cols-2">
                      <DiffSection title="Header fields" diff={diff.header} />
                      <DiffSection title="Constants" diff={diff.constants} />
                      <DiffSection title="Line columns" diff={diff.lines} />
                      <DiffSection title="Excluded" diff={diff.excluded && { added: diff.excluded.added, removed: diff.excluded.removed }} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {applyResult && (
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-sm text-emerald-800 dark:text-emerald-200 space-y-1">
                <div className="flex items-center gap-2 font-medium">
                  <ShieldCheck className="h-4 w-4" />
                  {applyResult.created ? 'Template created' : 'Template updated'}: {applyResult.template?.name}
                </div>
                <div>
                  {analyzerState === 'building' && <span className="inline-flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Building the analyzer…</span>}
                  {analyzerState === 'built' && 'Analyzer built — the template is ready for documents.'}
                  {analyzerState && analyzerState !== 'building' && analyzerState !== 'built' && <span className="text-amber-700 dark:text-amber-300">{analyzerState}</span>}
                  {!analyzerState && 'Build the analyzer from the template page before sending documents.'}
                </div>
              </div>
            )}
          </div>

          <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={buildAnalyzer} onChange={(e) => setBuildAnalyzer(e.target.checked)} disabled={Boolean(applyResult)} />
              Build the analyzer afterwards
            </label>
            <div className="flex gap-2">
              <button type="button" className={btnSecondary} onClick={onClose}>{applyResult ? 'Close' : 'Cancel'}</button>
              {!applyResult && (
                <button type="button" className={btnSecondary} onClick={handleValidate} disabled={!canValidate}>
                  {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Validate
                </button>
              )}
              {!applyResult && (
                <button type="button" className={btnPrimary} onClick={handleApply} disabled={!canApply}>
                  {applying && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isUpdate ? 'Update template' : 'Create template'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
